import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import {
  rmaFormFor,
  rmaNo,
  type DeviceRmaRequest,
  type RmaDeviceModel,
  type RmaReturnStatus,
} from '@staff-app/shared-types';
import { ExternalLink } from 'lucide-react';
import { CopyButton } from '@/components/CopyButton';
import { DATA_TABLE_CELLS, DATA_TABLE_HEADER } from '@/lib/data-table';

/**
 * Minimal HTML escape for the ONE place this file builds markup — the rich clipboard flavour.
 *
 * `CopyButton` puts `html` on the clipboard verbatim and says callers must escape it. A tracking
 * number is carrier-supplied and a URL is Shopify-supplied, so neither is ours to trust blindly
 * into markup that will be pasted into a ticket system.
 */
function escapeHtml(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fmtDate(d?: string | null): string {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(+parsed) ? '—' : parsed.toLocaleDateString();
}

/** Short, scannable labels. The full name is on the subject, which is its own column. */
const MODEL_LABEL: Record<RmaDeviceModel, string> = {
  OPTISTICK: 'OptiStick',
  PRO: 'Pro Player',
  PROMAX: 'ProMax Player',
  UNKNOWN: 'Unknown',
};

/**
 * The model badge — and, in its tone and tooltip, HOW MUCH TO TRUST IT.
 *
 * The model is derived, not recorded: HubSpot has no product property on the ticket. It comes from
 * the SOURCE FORM where that is known, and otherwise from parsing the subject line. Those are not
 * equally reliable, and the difference matters at exactly the moment someone is deciding a
 * restocking fee ($50 OptiStick / $110 Pro / $220 ProMax): the form is a choice the customer made
 * and cannot go back and change, while the subject is a text input they can type anything into.
 *
 * So there are three tones, in descending confidence, and the tooltip always names the source:
 *
 *   secondary   from the form — authoritative
 *   outline     inferred from the subject — plausible, not proof
 *   destructive UNKNOWN — neither signal was readable; go and look at the ticket
 *
 * A dedicated Form column was considered and rejected: form and model are 1:1, so it would have
 * restated this badge in every row. The provenance belongs ON the value it qualifies.
 */
function ModelBadge({ row }: { row: DeviceRmaRequest }) {
  const model = row.deviceModel as RmaDeviceModel;
  const form = rmaFormFor(row.sourceFormId);
  const fromForm = Boolean(form) && form?.model === model;

  const variant = model === 'UNKNOWN' ? 'destructive' : fromForm ? 'secondary' : 'outline';
  const title = fromForm
    ? `Submitted through the ${form?.label} — the model is what the customer selected, not a guess`
    : model === 'UNKNOWN'
      ? 'Neither the form nor the subject identified a model — open the ticket before quoting a fee'
      : 'Inferred from the ticket subject, which the customer can edit — confirm before quoting a fee';

  return (
    <Badge
      variant={variant as never}
      title={title}
      data-testid={`rma-model-${model}`}
      data-model-source={fromForm ? 'form' : 'subject'}
    >
      {MODEL_LABEL[model] ?? model}
    </Badge>
  );
}

/**
 * The verify control — and, now, the verification STATE.
 *
 * The indicator dot was removed (requester, 2026-08-25), so the button has to say where the row is
 * as well as what pressing it does. That is the same shape the Return control already had, and the
 * two now read alike: a button whose LABEL is the state and whose TONE is how much it wants
 * attention.
 *
 *   outline    not verified — the working queue, something to act on
 *   secondary  verified — settled, still pressable to review or retract
 *
 * `outline` rather than `destructive` for unverified: an unchecked RMA is ordinary work, not an
 * error. Red was overstating it, which is part of why the dot went.
 *
 * The accountability trail moves onto the button with it — `title` still carries who verified and
 * when, because that is what someone reads when a replacement shipment is questioned.
 */
function VerifyButton({
  row,
  onVerify,
}: {
  row: DeviceRmaRequest;
  onVerify: (r: DeviceRmaRequest) => void;
}) {
  return (
    <Button
      variant={row.verified ? 'secondary' : 'outline'}
      size="sm"
      onClick={() => onVerify(row)}
      title={
        row.verified
          ? `Eligibility verified by ${row.verifiedBy ?? 'unknown'} on ${fmtDate(row.verifiedAt)}`
          : 'Not verified — nobody has checked the warranty window, order number or receipt yet'
      }
      data-verified={row.verified ? 'yes' : 'no'}
      data-testid={`rma-verify-${row.submissionId}`}
    >
      {row.verified ? 'Verified' : 'Verify'}
    </Button>
  );
}

/**
 * The order number to ACT ON — the staffer's correction when there is one, not the raw submission.
 *
 * `orderNumber` is free text the customer typed into the HubSpot form and is routinely partial or
 * wrong; the verifier looks it up against Shopify and stores the right one in `previousOrderNumber`
 * (RmaVerifyDialog explains why that is a separate column rather than an overwrite — what the
 * customer submitted has to survive as evidence of the discrepancy).
 *
 * The queue shows the corrected value BECAUSE that is the one anybody downstream uses: it is what
 * gets pasted into Shopify to raise the replacement, and a column still showing the bad number
 * after someone has already done the work of fixing it just gets that work done twice.
 *
 * The submission is not thrown away, it is demoted — dotted underline plus the tooltip, the same
 * "corrected, and here is what it was" convention the verify dialog uses. A second line under the
 * value was considered and rejected: this column is scanned, and the discrepancy matters to the one
 * person opening the row, not to the twenty rows above it.
 */
function OrderNumberCell({ row }: { row: DeviceRmaRequest }) {
  const submitted = (row.orderNumber ?? '').trim();
  const corrected = (row.previousOrderNumber ?? '').trim();
  const value = corrected || submitted;

  if (!value) return <span className="text-muted-foreground">{'\u2014'}</span>;

  // Only a DIFFERENT correction is worth marking. Confirming the customer's number by retyping it
  // unchanged is the common case and says nothing.
  const changed = Boolean(corrected) && corrected !== submitted;

  return (
    <span
      data-testid="rma-cell-order-value"
      data-order-corrected={changed ? 'yes' : 'no'}
      className={changed ? 'underline decoration-dotted underline-offset-4' : undefined}
      title={
        changed
          ? `Corrected during verification. Customer submitted: ${submitted || '\u2014'}`
          : undefined
      }
    >
      {value}
    </span>
  );
}

/**
 * The quantity to ACT ON — the verifier's correction when there is one.
 *
 * The exact twin of OrderNumberCell, and deliberately so: `deviceCount` is the customer's own
 * answer, `verifiedDeviceCount` is what a human confirmed we owe (migration 046), and the queue
 * shows the second because it is the number that reaches Shopify. The customer's stays in the
 * tooltip, because a discrepancy here is worth seeing — somebody asked for two devices and we
 * agreed to one.
 */
function QtyCell({ row }: { row: DeviceRmaRequest }) {
  const submitted = row.deviceCount ?? null;
  const corrected = row.verifiedDeviceCount ?? null;
  const value = corrected ?? submitted;

  if (value == null) return <span className="text-muted-foreground">{'\u2014'}</span>;

  const changed = corrected != null && corrected !== submitted;

  return (
    <span
      data-testid="rma-cell-qty-value"
      data-qty-corrected={changed ? 'yes' : 'no'}
      className={changed ? 'underline decoration-dotted underline-offset-4' : undefined}
      title={
        changed
          ? `Corrected during verification. Customer submitted: ${submitted ?? '\u2014'}`
          : undefined
      }
    >
      {value}
    </span>
  );
}

/**
 * The replacement order, in the queue — because "has this shipped?" is a scanning question.
 *
 * It sits immediately right of Verification (requester, 2026-09-21) and that placement is the
 * answer to a sequence, read left to right: is it eligible, did we send one, where is it. Before
 * this, the order existed only inside the verify dialog, so the only way to answer it for twenty
 * rows was to open twenty dialogs.
 *
 * A `manual` order is marked. The two are not equally known: an order this app created carries a
 * SKU, quantity and address we chose, while a recorded one is a number a human checked against
 * Shopify and nothing more.
 */
function ReplacementOrderCell({ row }: { row: DeviceRmaRequest }) {
  if (!row.replacementOrderId) return <span className="text-muted-foreground">{'\u2014'}</span>;
  const label = row.replacementOrderName || 'order';
  return (
    <span className="inline-flex items-center gap-1">
      <a
        href={row.replacementOrderUrl ?? undefined}
        target="_blank"
        rel="noreferrer"
        // The row itself opens the drawer; a link inside it must not do both.
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 font-mono text-sm underline"
        title={
          row.replacementOrderManual
            ? `Recorded by hand by ${row.replacementOrderedBy ?? 'unknown'} — staff-app did not create this order`
            : `Created by staff-app for ${row.replacementOrderedBy ?? 'unknown'}`
        }
        data-manual={row.replacementOrderManual ? 'yes' : 'no'}
        data-testid="rma-cell-replacement-order"
      >
        {label}
        {row.replacementOrderManual ? <span className="text-muted-foreground">{'*'}</span> : null}
      </a>
      {/* Copies the ORDER NAME alone — `#28861`, never the asterisk that marks a hand-recorded one
          and never the gid. That string is what Shopify's own search takes and what a person says
          out loud; the `*` is a mark for this table and would be noise anywhere it was pasted.

          Rich flavour carries the admin link, so a number pasted into a ticket is one click from
          the order for whoever reads it next. */}
      <CopyButton
        value={label}
        html={
          row.replacementOrderUrl
            ? `<a href="${escapeHtml(row.replacementOrderUrl)}">${escapeHtml(label)}</a>`
            : undefined
        }
        label="Order number"
        testId="rma-copy-replacement-order"
        stopPropagation
      />
    </span>
  );
}

/**
 * The tracking number, linked when the carrier gave Shopify a URL.
 *
 * Deliberately NOT truncated. A tracking number is only useful whole — half of one cannot be
 * pasted into a carrier's site, and this is the column somebody reads out on the phone.
 *
 * AN ORDER WITH NO TRACKING READS "not shipped", NOT "—". Creating the order does not ship it:
 * Shopify still needs a shipping label, made by a human in the admin. A bare dash there says "no
 * value", and the thing an operator actually needs to see while scanning is that this one is
 * waiting on somebody — it is the difference between a replacement that is with the customer and
 * one that is sitting in a queue nobody has looked at.
 */
function ReplacementTrackingCell({ row }: { row: DeviceRmaRequest }) {
  const n = row.replacementTrackingNumber;
  if (!n) {
    return row.replacementOrderId ? (
      <span
        className="text-sm text-muted-foreground"
        title="The order exists but no shipping label has been made — create it in Shopify, then the tracking number lands here"
        data-testid="rma-cell-replacement-unshipped"
      >
        {'not shipped'}
      </span>
    ) : (
      <span className="text-muted-foreground">{'\u2014'}</span>
    );
  }
  // COPYABLE, because pasting is what actually happens to this value — into a ticket reply, into
  // the carrier's own site, into a message to the customer. Retyping eighteen characters off a
  // screen is how a tracking number arrives one digit wrong at the other end.
  //
  // `stopPropagation` is load-bearing here (CopyButton documents it): the row opens the detail
  // drawer, and copying must not also open a panel nobody asked for.
  //
  // The RICH flavour carries the carrier's link when Shopify gave us one, so a number pasted into
  // a ticket arrives as something the next agent can click rather than a bare string they have to
  // hand-link. The plain flavour is always the number alone — most targets take only that one.
  const copy = (
    <CopyButton
      value={n}
      html={
        row.replacementTrackingUrl
          ? `<a href="${escapeHtml(row.replacementTrackingUrl)}">${escapeHtml(n)}</a>`
          : undefined
      }
      label="Tracking number"
      testId="rma-copy-tracking"
      stopPropagation
    />
  );

  return (
    <span className="inline-flex items-center gap-1">
      {row.replacementTrackingUrl ? (
        <a
          href={row.replacementTrackingUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="font-mono text-sm underline"
          data-testid="rma-cell-replacement-tracking"
        >
          {n}
        </a>
      ) : (
        <span className="font-mono text-sm" data-testid="rma-cell-replacement-tracking">
          {n}
        </span>
      )}
      {copy}
    </span>
  );
}

const RETURN_LABEL: Record<RmaReturnStatus, string> = {
  AWAITING: 'Awaiting',
  RECEIVED_TESTING: 'Testing',
  RECEIVED: 'Received',
  RECEIVED_INCOMPLETE: 'Missing parts',
};

/**
 * Has the faulty unit come back?
 *
 * Four states, and the tone carries which one needs attention. AWAITING means two different
 * things and only one of them is a clock: before verification nothing has shipped, so there is
 * nothing to wait for and it renders muted; AFTER verification a replacement is already with the
 * customer and the 30-day return deadline is running, so it renders as an outstanding item.
 *
 * `Missing parts` is not a lesser "received" — manual-replacement.md lists what must come back
 * (stick, remote, HDMI adapter, power adapter) and missing items are deducted, so it is its own
 * outcome with money attached, and it is toned to be noticed rather than blend into Received.
 */
function ReturnStatusButton({
  row,
  onReturn,
}: {
  row: DeviceRmaRequest;
  onReturn: (row: DeviceRmaRequest) => void;
}) {
  const status = row.returnStatus as RmaReturnStatus;
  const outstanding = status === 'AWAITING' && row.verified;

  // Same tone vocabulary as VerifyButton, so the two controls read as one family:
  //   outline      outstanding — a replacement went out and the unit is not back; the clock runs,
  //                or it is back but still on the bench (RECEIVED_TESTING) — not settled either
  //   secondary    settled (received, or nothing shipped yet so nothing is owed)
  //   destructive  missing parts — a different financial outcome, and the one to notice
  const variant: 'secondary' | 'destructive' | 'outline' =
    status === 'RECEIVED'
      ? 'secondary'
      : status === 'RECEIVED_INCOMPLETE'
        ? 'destructive'
        : status === 'RECEIVED_TESTING' || outstanding
          ? 'outline'
          : 'secondary';

  const title =
    status === 'AWAITING'
      ? outstanding
        ? 'A replacement has gone out and the faulty unit has not come back yet — the 30-day return deadline is running'
        : 'Nothing has shipped yet, so there is nothing to wait for'
      : `${RETURN_LABEL[status]} by ${row.returnedBy ?? 'unknown'} on ${fmtDate(row.returnedAt)}`;

  // THE STATUS IS THE BUTTON. A separate "Record" control beside a badge was tried and rejected as
  // two controls for one fact. Now a real Button rather than a badge dressed as one (requester,
  // 2026-08-25) — it should look pressable, because it is.
  return (
    <Button
      variant={variant}
      size="sm"
      onClick={() => onReturn(row)}
      title={title}
      data-testid={`rma-return-${status}`}
      data-outstanding={outstanding ? 'yes' : 'no'}
      className={!outstanding && status === 'AWAITING' ? 'opacity-60' : undefined}
    >
      {RETURN_LABEL[status] ?? status}
    </Button>
  );
}

/**
 * Has a restocking fee been raised, and where is it?
 *
 * READ-ONLY on purpose. Raising one happens in the return dialog, behind a resolved Stripe customer
 * and a written reason; a second entry point from the table would be a way to charge a customer
 * with less deliberation, which is the opposite of what that gate is for.
 *
 * The amount is the label because it is the question — "did we charge them, and how much" — and it
 * links straight to the DRAFT in Stripe, because the draft still has to be finalized by hand and
 * that is the next thing anyone does with it.
 *
 * An em dash rather than "None": most rows never carry a fee, and a column of the word "None"
 * reads as noise where an empty cell reads as nothing to see.
 */
function RestockingFeeCell({ row }: { row: DeviceRmaRequest }) {
  if (!row.restockingInvoiceId) {
    return (
      <span
        className="text-sm text-muted-foreground"
        data-testid={`rma-fee-cell-${row.submissionId}`}
      >
        {'—'}
      </span>
    );
  }
  const amount =
    row.restockingFeeMinor != null ? `$${(row.restockingFeeMinor / 100).toFixed(2)}` : 'Raised';
  const title = `Draft invoice ${row.restockingInvoiceId} — raised by ${
    row.restockingInvoicedBy ?? 'unknown'
  } on ${fmtDate(row.restockingInvoicedAt)}. Still a DRAFT: finalize it in Stripe.`;

  // Rows charged before migration 029 captured the url have the id but no link — show the amount
  // plainly rather than a dead anchor.
  if (!row.restockingInvoiceUrl) {
    return (
      <span className="text-sm" title={title} data-testid={`rma-fee-cell-${row.submissionId}`}>
        {amount}
      </span>
    );
  }
  return (
    <a
      href={row.restockingInvoiceUrl}
      target="_blank"
      rel="noreferrer"
      title={title}
      className="inline-flex items-center gap-1 text-sm underline"
      data-testid={`rma-fee-cell-${row.submissionId}`}
    >
      {amount}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

/**
 * The RMA request ledger.
 *
 * Every column is somebody's question at the moment they open this page: which model and how many
 * (that is what a replacement costs), who the customer is and what order they are quoting (that is
 * what makes the request checkable), whether the receipt is attached (that is what they check it
 * against), and whether anyone has checked it yet.
 *
 * What is NOT a column, and why. The full ship-to address and the customer's own description of the
 * fault are both long, and neither is what this table is for — it is a queue for deciding
 * eligibility, and the address only matters once that decision is made. Both live one click away in
 * RmaDetailDrawer.
 *
 * Two more were REMOVED after the move to form-submission ingest (requester, 2026-08-25), because
 * each had become a column of dashes:
 *   - "From" (`orderLocation`) — the sandbox forms carry no `rma_order_location` property, so it is
 *     empty in every non-prod environment. Still shown in the drawer, where it is populated in prod.
 *   - "Ticket" (`hubspotTicketId`) — a submission creates no ticket in a sandbox portal, and the
 *     ledger is keyed on the submission now, so this is null on essentially every row. Dropped from
 *     the drawer too (requester, 2026-08-26); the column and the field are still stored.
 * A column that cannot vary is a column nobody reads — the same argument that retired Ship-to from
 * the demo-coupon table.
 */
export function RmaTable({
  requests,
  onOpen,
  onVerify,
  onReturn,
}: {
  requests: DeviceRmaRequest[];
  onOpen: (row: DeviceRmaRequest) => void;
  onVerify: (row: DeviceRmaRequest) => void;
  onReturn: (row: DeviceRmaRequest) => void;
}) {
  if (!requests.length) {
    return (
      <p className="p-6 text-sm text-muted-foreground" data-testid="rma-empty">
        {'No RMA requests yet.'}
      </p>
    );
  }

  return (
    <Table className={DATA_TABLE_CELLS} data-testid="rma-table">
      <TableHeader className={DATA_TABLE_HEADER}>
        <TableRow>
          {/* LEFTMOST, because it is the row's identity (requester, 2026-09-21). Everything else in
              this table describes the request; this names it. It is also the only column an
              operator can carry away — into a ticket subject, onto a phone call — which a GUID
              never was. */}
          <TableHead className="whitespace-nowrap">{'RMA #'}</TableHead>
          <TableHead>{'Model'}</TableHead>
          <TableHead className="text-right">{'Qty'}</TableHead>
          <TableHead>{'Submit by'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Order #'}</TableHead>
          <TableHead className="whitespace-nowrap text-center">{'Receipt'}</TableHead>
          {/* THE STATE AND THE CONTROL THAT CHANGES IT NOW LIVE TOGETHER (requester, 2026-08-25).
              There is no separate Actions column any more: each of these two carries its own
              indicator and its own button, so "which of these need checking" is still answerable by
              scanning, and the thing you press is next to the thing it changes rather than three
              columns away. The two questions stay distinct — Verification is "may we send a
              replacement", Return status is "did the old unit come back" — and they are weeks
              apart. */}
          <TableHead className="whitespace-nowrap text-center">{'Verification'}</TableHead>
          {/* Right of Verification (requester, 2026-09-21): eligible → sent → where is it. The
              replacement pair reads as the consequence of the verification beside it, and both are
              about the OUTBOUND device — which is why they sit before Return status rather than
              next to it. */}
          <TableHead className="whitespace-nowrap">{'Replacement order #'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Tracking'}</TableHead>
          <TableHead className="whitespace-nowrap">{'Return status'}</TableHead>
          {/* Right of Return status (requester, 2026-08-25), because it is downstream of it: a fee is
              raised after a unit comes back and is found fine, or fails to come back at all. Read
              only — the charge is made in the return dialog, and putting a second way to raise one
              in the table is the duplication the Record button already died for. */}
          <TableHead className="whitespace-nowrap">{'Restocking fee'}</TableHead>
          {/* LAST (requester, 2026-09-21). It was second, beside the identity, which gave the most
              prominent real estate in the table to a date nobody sorts or filters by: the queue is
              already newest-first, so the order of the rows carries what the column was saying. It
              earns its place as the thing you check once — how long this has been waiting — rather
              than as something read on every row. */}
          <TableHead className="whitespace-nowrap">{'Submitted'}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((r) => (
          <TableRow
            key={r.submissionId}
            data-testid={`rma-row-${r.submissionId}`}
            className="cursor-pointer"
            onClick={() => onOpen(r)}
          >
            {/* Copyable for the same reason as the order and tracking numbers: this is the value
                that gets pasted into a ticket. `rmaNo` is shared with the drawer so the two can
                never disagree about how a request is named. */}
            <TableCell className="whitespace-nowrap font-mono">
              <span className="inline-flex items-center gap-1">
                <span data-testid="rma-cell-number">{rmaNo(r.rmaNumber)}</span>
                {r.rmaNumber != null ? (
                  <CopyButton
                    value={rmaNo(r.rmaNumber)}
                    label="RMA number"
                    testId="rma-copy-number"
                    stopPropagation
                  />
                ) : null}
              </span>
            </TableCell>

            <TableCell data-testid="rma-cell-model">
              <ModelBadge row={r} />
            </TableCell>
            <TableCell className="text-right" data-testid="rma-cell-qty">
              <QtyCell row={r} />
            </TableCell>
            <TableCell data-testid="rma-cell-customer">{r.customerEmail}</TableCell>
            <TableCell className="whitespace-nowrap font-mono" data-testid="rma-cell-order">
              <OrderNumberCell row={r} />
            </TableCell>
            {/* The proof of purchase is the thing a verifier actually opens, so it is one click
                from the queue. HubSpot signs these urls and they expire — hence the plain link
                rather than an embed, and hence "Ticket" beside it as the durable way in. */}
            <TableCell className="whitespace-nowrap text-center">
              {r.proofOfPurchaseUrl ? (
                <a
                  href={r.proofOfPurchaseUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-sm underline"
                  data-testid={`rma-receipt-${r.submissionId}`}
                >
                  {'Open'}
                </a>
              ) : (
                <span className="text-sm text-muted-foreground">{'—'}</span>
              )}
            </TableCell>
            {/* Just the light — the same h-2 w-2 dot and semantic tokens as the customer-document
                verification mark. The state carries in colour; aria-label keeps it available to a
                screen reader and title puts the whole story (who verified, when) on hover. */}
            {/* stopPropagation on both state cells: the row opens the detail drawer, and neither
                dialog may open behind it. */}
            <TableCell className="whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
              <VerifyButton row={r} onVerify={onVerify} />
            </TableCell>
            {/* Read-only, both of them. They are set in the verify dialog — one control per fact,
                the rule the Record button died for. */}
            <TableCell className="whitespace-nowrap">
              <ReplacementOrderCell row={r} />
            </TableCell>
            <TableCell className="whitespace-nowrap">
              <ReplacementTrackingCell row={r} />
            </TableCell>
            <TableCell className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
              <ReturnStatusButton row={r} onReturn={onReturn} />
            </TableCell>
            <TableCell className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
              <RestockingFeeCell row={r} />
            </TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">
              {fmtDate(r.submittedAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
